import { useNavigate } from "react-router-dom";
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from "@heroicons/react/24/outline";

interface KPICardProps {
  label: string;
  value: string | number;
  trend?: number;
  trendLabel?: string;
  icon?: React.ReactNode;
  color?: "blue" | "emerald" | "amber" | "rose" | "violet" | "cyan" | "teal" | "slate";
  href?: string;
  /** Optional tiny explainer line rendered under the value. Used for
   *  cards whose label can be ambiguous (e.g. distinguishing Listing vs
   *  Property on the dashboard). Keep to ≤2 short sentences. */
  subLabel?: string;
}

const colorMap = {
  blue: "from-blue-500 to-blue-600 text-white",
  emerald: "from-emerald-500 to-emerald-600 text-white",
  amber: "from-amber-400 to-amber-500 text-white",
  rose: "from-rose-500 to-rose-600 text-white",
  violet: "from-violet-500 to-violet-600 text-white",
  cyan: "from-cyan-500 to-cyan-600 text-white",
  teal: "from-teal-500 to-teal-600 text-white",
  slate: "border border-gray-200 bg-white text-gray-900",
};

export function KPICard({ label, value, trend, trendLabel, icon, color, href, subLabel }: KPICardProps) {
  const navigate = useNavigate();
  const isPositive = trend !== undefined && trend >= 0;
  const gradient = color ? colorMap[color] : colorMap.slate;
  const isColored = color && color !== "slate";

  return (
    <div
      className={`rounded-lg px-2.5 py-1.5 shadow-sm transition-transform duration-200 ${
        href ? "cursor-pointer hover:scale-[1.02] hover:shadow-md" : ""
      } ${isColored ? `bg-gradient-to-br ${gradient}` : gradient}`}
      onClick={href ? () => navigate(href) : undefined}
    >
      <div className="flex items-center gap-2">
        {icon && (
          <div
            className={`flex-shrink-0 ${
              isColored ? "text-white/70" : "text-gray-400"
            } [&>svg]:h-5 [&>svg]:w-5`}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p
            className={`truncate text-[10px] font-semibold uppercase tracking-wide leading-tight ${
              isColored ? "text-white/80" : "text-gray-500"
            }`}
          >
            {label}
          </p>
          <div className="flex items-baseline gap-1.5">
            <p className="truncate text-base font-bold leading-tight">
              {value}
            </p>
            {trend !== undefined && (
              <span
                className={`flex items-center gap-0.5 text-[10px] font-medium ${
                  isColored
                    ? "text-white/80"
                    : isPositive
                    ? "text-emerald-600"
                    : "text-rose-600"
                }`}
              >
                {isPositive ? (
                  <ArrowTrendingUpIcon className="h-3 w-3" />
                ) : (
                  <ArrowTrendingDownIcon className="h-3 w-3" />
                )}
                {isPositive ? "+" : ""}
                {trend}%
                {trendLabel && (
                  <span
                    className={
                      isColored ? "text-white/60" : "text-gray-400"
                    }
                  >
                    {trendLabel}
                  </span>
                )}
              </span>
            )}
          </div>
          {subLabel && (
            <p
              className={`mt-0.5 text-[9px] leading-snug ${
                isColored ? "text-white/70" : "text-gray-500"
              }`}
              title={subLabel}
            >
              {subLabel}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
